import React from 'react';
import { Dayjs } from 'dayjs';
import { Text } from '../Text';
import { Flex } from '../Flex';
import { Combobox } from '../Combobox';
import { slugify } from '../../utils/helpers';

const getHourItems = (mode: string) => {
  const limit = mode === '12h' ? 12 : 24;
  const gap = mode === '12h' ? 1 : 0;
  return Array.from(Array(limit), (_, i) => `0${i + gap}`.slice(-2));
};

const minsItems = Array.from(Array(60), (_, i) => `0${i}`.slice(-2));
const periodItems = ['AM', 'PM'];
const inputStyles = { input: { maxWidth: 75 } };

interface TimePickerProps {
  date?: Dayjs;
  label?: string;
  mode?: '12h' | '24h';
  disableSelection: (day: Dayjs) => boolean;
  onTimeUpdate: (date?: Dayjs) => void;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  date,
  onTimeUpdate,
  mode = '24h',
  label = '',
  disableSelection,
}) => {
  const is12Hours = mode === '12h';
  const hour = is12Hours ? date?.format('hh') || '01' : date?.format('HH') || '00';
  const min = date?.format('mm') || '00';
  const period = date?.format('A') || 'AM';

  const hoursLabel = React.useMemo(() => `${label} Hours`.trim(), [label]);
  const minutesLabel = React.useMemo(() => `${label} Minutes`.trim(), [label]);
  const periodLabel = React.useMemo(() => `${label} Period`.trim(), [label]);

  const hourItems = getHourItems(mode);

  const onChangeHours = React.useCallback(
    (newHour: string) => {
      if (!date) {
        return;
      }

      let hourOfDay = parseInt(newHour, 10);
      if (mode === '12h') {
        if (period === 'AM') {
          hourOfDay = hourOfDay === 12 ? 0 : hourOfDay;
        } else {
          hourOfDay = hourOfDay === 12 ? 12 : hourOfDay + 12;
        }
      }
      onTimeUpdate(date.hour(hourOfDay));
    },
    [date, mode, onTimeUpdate, period],
  );

  const onChangeMinutes = React.useCallback(
    (minutes: string) => {
      if (!date) {
        return;
      }
      onTimeUpdate(date.minute(parseInt(minutes, 10)));
    },
    [date, onTimeUpdate],
  );

  const onChangePeriod = React.useCallback(
    (newPeriod: string) => {
      if (!date) {
        return;
      }

      const diff = newPeriod === 'AM' ? -12 : 12;
      onTimeUpdate(date.hour(date.hour() + diff));
    },
    [date, onTimeUpdate],
  );

  return (
    <Flex align="center" justify="center" gap={2} sx={inputStyles}>
      {label && !is12Hours && (
        <Text as="label" flexShrink={0} role="status" pr={3}>
          {label}
        </Text>
      )}
      <Combobox
        id={slugify(hoursLabel)}
        searchable
        onChange={onChangeHours}
        label={hoursLabel}
        hideLabel
        items={hourItems}
        value={hour}
        disableItem={(hourItem: string) =>
          date && disableSelection(date.hour(parseInt(hourItem, 10)))
        }
        showClearSelectionControl={false}
        sx={{
          '& .MuiInputBase-input.MuiInputBase-input.MuiAutocomplete-input': {
            padding: '7.5px 4px 7.5px 5px',
          },
        }}
      />

      <Combobox
        id={slugify(minutesLabel)}
        searchable
        onChange={onChangeMinutes}
        label={minutesLabel}
        hideLabel
        items={minsItems}
        value={min}
        disableItem={(minuteItem: string) =>
          date && disableSelection(date.minute(parseInt(minuteItem, 10)))
        }
        showClearSelectionControl={false}
        sx={{
          '& .MuiInputBase-input.MuiInputBase-input.MuiAutocomplete-input': {
            padding: '7.5px 4px 7.5px 5px',
          },
        }}
      />

      {is12Hours && (
        <Combobox
          id={slugify(periodLabel)}
          onChange={onChangePeriod}
          label={periodLabel}
          hideLabel
          items={periodItems}
          value={period}
          showClearSelectionControl={false}
          sx={{
            minWidth: '85px',
            '& .MuiOutlinedInput-input.MuiSelect-select': {
              padding: '16px 14px',
            },
          }}
        />
      )}
    </Flex>
  );
};

export default React.memo(TimePicker);
